import type { SetStateAction } from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { QueryParamsObjectConfig } from '../query-params.ts';
import { generateQueryParamsString, parseQueryParamsString } from '../query-params.ts';
import { useMemoDeepEqual } from './use-memo-deep-equal.ts';

export type SetValue<Value> = (value: SetStateAction<Partial<Value>>, action?: 'replace' | 'push') => void;
type Result<T> = [T, SetValue<T>];

export function useQueryParams<TValue extends Record<string, unknown>>(
    config: QueryParamsObjectConfig<TValue>,
): Result<Partial<TValue>>;

export function useQueryParams<
    TValue extends Record<string, unknown>,
    TDefaultValues extends Partial<TValue>,
>(
    config: QueryParamsObjectConfig<TValue>,
    defaultValues: TDefaultValues,
): Result<Partial<TValue> & TDefaultValues>;

export function useQueryParams<TValue extends Record<string, unknown>>(
    config: QueryParamsObjectConfig<TValue>,
    defaultValues?: TValue,
): Result<Partial<TValue>> {
    const [params, setParams] = useSearchParams();

    const value = useMemoDeepEqual(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- legacy code
        () => parseQueryParamsString(params, config, defaultValues!),
        [params, config],
    );

    const paramsRef = useRef(params);
    const valueRef = useRef<Partial<TValue>>(value);
    const defaultValuesRef = useRef(defaultValues);

    useLayoutEffect(() => {
        paramsRef.current = params;
        valueRef.current = value;
        defaultValuesRef.current = defaultValues;
    }, [params, value, defaultValues]);

    const setValue = useCallback<SetValue<TValue>>(
        (nextValueOrFn, action = 'replace') => {
            const nextValue: Partial<TValue> =
                typeof nextValueOrFn === 'function' ? nextValueOrFn(valueRef.current) : nextValueOrFn;

            const query = generateQueryParamsString(config, nextValue, paramsRef.current);

            // The navigation lands asynchronously and the refs above only resync on the
            // next layout effect, so a second write in the same tick would build on the
            // params from before this one and silently drop it — a tab change clicked
            // while the page is still writing its own defaults, say. Carry the pending
            // write forward so writes in the same tick compose instead of clobbering.
            const pendingParams = new URLSearchParams(query);
            const pendingDefaults = defaultValuesRef.current;
            paramsRef.current = pendingParams;
            valueRef.current = pendingDefaults
                ? parseQueryParamsString(pendingParams, config, pendingDefaults)
                : parseQueryParamsString(pendingParams, config);

            setParams(query, { replace: action === 'replace' });
        },
        [config, setParams],
    );

    return [value, setValue];
}
